import { memo } from 'react';

import {
  Button,
  Divider,
  ExcelIcon,
  HelpCircleFillIcon,
  Text,
  Tooltip,
  type TypeTextProps,
} from '@thefarmersfront/kitchen';

import { Bullet, SubBullet } from '~/components/common';

import { formatNumber } from '~/utils';

import type { TypeGetStorageFeeMonthlyResponse } from '~/types/settlement.types';

import styles from './StorageMonthlySummary.module.scss';

type TypeStorageMonthlySummaryProps = {
  data: TypeGetStorageFeeMonthlyResponse['data']['total'];
  onDownloadExcel: () => Promise<void>;
};

const StorageMonthlySummary = ({ data, onDownloadExcel }: TypeStorageMonthlySummaryProps) => {
  return (
    <>
      <div className={styles['billing-summary']}>
        <div className={styles['billing-summary-title']}>
          <Text weight="bold">정산 예정 금액</Text>
        </div>
        <div className={styles['billing-summary-contents']}>
          <SummaryItem
            label="총 보관비"
            amount={{ value: data.totalStorageFee, weight: 'semi-bold', color: 'secondary' }}
          />
          <SummaryItem
            label="면제 금액"
            amount={{ value: data.unchargedStorageFee.total, weight: 'semi-bold', color: 'point' }}
          />
          <div className={styles['exemption-details']}>
            {data.unchargedStorageFee.RANGE_001 && (
              <SummaryItem label="30일 이하 면제" amount={{ value: data.unchargedStorageFee.RANGE_001 }} depth={2} />
            )}
            {data.unchargedStorageFee.RANGE_002 && (
              <SummaryItem
                label="31일~60일 이하 면제"
                amount={{ value: data.unchargedStorageFee.RANGE_002 }}
                depth={2}
              />
            )}
            {data.unchargedStorageFee.RANGE_003 && (
              <SummaryItem
                label="61일~90일 이하 면제"
                amount={{ value: data.unchargedStorageFee.RANGE_003 }}
                depth={2}
              />
            )}
            {data.unchargedStorageFee.RANGE_004 && (
              <SummaryItem label="91일 이상 면제" amount={{ value: data.unchargedStorageFee.RANGE_004 }} depth={2} />
            )}
          </div>
        </div>
        <Divider orientation="horizontal" color="gray100" />
        <div>
          <TotalSummary amount={data.chargedStorageFee} />
          <Text size="xsmall" color="tertiary" align="right">
            VAT 별도
          </Text>
        </div>
      </div>
      <div className={styles['summary-download']}>
        <Text size="small" color="secondary" element="span">
          상품별 보관비 내역
        </Text>
        <Button
          size="tiny"
          color="tertiary"
          variant="outlined"
          leftIcon={<ExcelIcon size={16} />}
          onClick={onDownloadExcel}
        >
          엑셀 다운로드
        </Button>
      </div>
    </>
  );
};

export default StorageMonthlySummary;

const SummaryItem = memo(
  ({
    label,
    amount: { value, weight = 'regular', color = 'tertiary' },
    depth = 1,
  }: {
    label: string;
    amount: { value: number; weight?: TypeTextProps['weight']; color?: TypeTextProps['color'] };
    depth?: 1 | 2;
  }) => {
    return (
      <div className={styles['billing-summary-item']}>
        <div className={styles['billing-summary-label']}>
          {depth === 2 && <SubBullet className={styles['sub-bullet']} fill="#848F9A" />}
          <Text size="small" color="secondary" element="span">
            {label}
          </Text>
        </div>
        <div>
          <Text weight={weight} color={color} element="span">
            {formatNumber(value)}
          </Text>
          <Text size="large" color={color} element="span">
            원
          </Text>
        </div>
      </div>
    );
  }
);

const TotalSummary = memo(({ amount }: { amount: number }) => {
  return (
    <div className={styles['billing-summary-item']}>
      <div className={styles['billing-summary-label']}>
        <Text size="small" element="span">
          실제 과금액
        </Text>
        <Tooltip
          className={styles['tooltip-icon']}
          placement="bottom-right"
          description={
            <>
              <TooltipDescriptionItem text="실제 과금액이란? 총 보관비 - 면제 금액" />
              <TooltipDescriptionItem text="보관비는 상품의 CBM, 보관 기간에 따라 책정됩니다." />
              <TooltipDescriptionItem text="30일 이하: 1,000원" depth={2} />
              <TooltipDescriptionItem text="31~60일: 2,000원" depth={2} />
              <TooltipDescriptionItem text="61~90일: 3,000원" depth={2} />
              <TooltipDescriptionItem text="91일 이상: 5,000원" depth={2} />
            </>
          }
        >
          <HelpCircleFillIcon size={16} color="gray400" />
        </Tooltip>
      </div>
      <div>
        <Text size="xxxlarge" weight="semi-bold" element="span">
          {formatNumber(amount)}
        </Text>
        <Text size="large" element="span">
          원
        </Text>
      </div>
    </div>
  );
});

const TooltipDescriptionItem = memo(({ text, depth = 1 }: { text: React.ReactNode; depth?: 1 | 2 }) => {
  return (
    <div className={`${styles['tooltip-description']} ${styles[`depth-${depth}`]}`}>
      <Bullet className={styles['bullet']} fill="#ffffff" />
      <p>{text}</p>
    </div>
  );
});

SummaryItem.displayName = 'SummaryItem';
TotalSummary.displayName = 'TotalSummary';
TooltipDescriptionItem.displayName = 'TooltipDescriptionItem';
